import type { Key9Puzzle } from '../types/puzzle';
import {
  getSavedDayProgress,
  getSavedKey9Result,
  type DayProgress,
  type DaySudokuSave
} from '../storage/localSave';
import { getInitialDayFlow, type InitialDayFlow } from './dayFlow';
import { isSameDailyPuzzleIdentity, type DailyPuzzleIdentity } from './puzzleIdentity';

export type HydratedBootState = {
  progress: DayProgress | null;
  key9Result: DayProgress['key9Result'] | null;
  flow: InitialDayFlow;
  sceneStart: {
    puzzle: Key9Puzzle;
    mode: 'key9' | 'full';
    practice: boolean;
    progress?: DayProgress;
  };
  ignoredStaleSave: boolean;
  renderPlan: ['scene:start:hydrated'];
};

export function createHydratedBootState(input: {
  puzzle: Key9Puzzle;
  identity: DailyPuzzleIdentity;
  save: DaySudokuSave;
  fallbackDate: string;
}): HydratedBootState {
  const progress = getSavedDayProgress(input.save, input.fallbackDate, input.identity);
  const key9Result = progress?.key9Result ?? getSavedKey9Result(input.save, input.fallbackDate, input.identity);
  const flow = getInitialDayFlow(progress);

  return {
    progress,
    key9Result,
    flow,
    sceneStart: {
      puzzle: input.puzzle,
      mode: flow.mode,
      practice: flow.practice,
      ...(progress ? { progress } : {})
    },
    ignoredStaleSave: progress == null && hasAnyStaleSaveForPuzzle(input.save, input.puzzle.date, input.identity),
    renderPlan: ['scene:start:hydrated']
  };
}

export function createBootSessionToken(activeBootToken: number): { activeBootToken: number; token: number } {
  const token = activeBootToken + 1;
  return { activeBootToken: token, token };
}

export function shouldApplyBootToken(activeBootToken: number, token: number): boolean {
  return activeBootToken === token;
}

function hasAnyStaleSaveForPuzzle(save: DaySudokuSave, puzzleDate: string, identity: DailyPuzzleIdentity): boolean {
  if (save.days[puzzleDate] != null || save.results[puzzleDate] != null) return true;
  return Object.values(save.days).some((progress) => (
    progress.identity != null
    && !isSameDailyPuzzleIdentity(progress.identity, identity)
    && (
      progress.identity.puzzleHash === identity.puzzleHash
      || progress.identity.puzzleId === identity.puzzleId
      || progress.identity.puzzleDate === identity.puzzleDate
    )
  ));
}
